import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, IconButton, TextField, Typography, styled, useTheme } from "@mui/material";
import { trackEvent } from "apps/web/src/reporting/reporting";
import React, { useState } from "react";
import { ErrorBox } from "../../../universal/ErrorBox";
import { FileOrImageDragAndDrop } from "../../../universal/FileUpload/FileOrImageDragAndDrop";
import { useUploadPhotoForAiQuery } from "../../../universal/Image/uploadPhoto";
import { formatRecipeImageFileSize } from "./formatRecipeImageFileSize";
import { TranscribeError } from "./utils";

export const ColumnFlexBox = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));

export const RowFlexBox = styled(Box)(({ theme: { spacing } }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  padding: spacing(1, 2),
}));

export type TranscribePresentationMode = "link" | "photo";

interface TranscribeWithGPTContentProps {
  presignedGetImageUrl: string;
  setPresignedGetImageUrl: (imageBe64String: string) => void;
  recipeUrl: string;
  onChangeRecipeUrl: (url: string) => void;
  invalidUrl: boolean;
  mode: TranscribePresentationMode;
  transcriberError: TranscribeError | null;
  setTranscriberError: (e: TranscribeError | null) => void;
  onError: (error: any) => void;
}

export const TranscribeWithGPTContent = ({
  presignedGetImageUrl,
  setPresignedGetImageUrl,
  recipeUrl,
  onChangeRecipeUrl,
  invalidUrl,
  mode,
  transcriberError,
  setTranscriberError,
  onError,
}: TranscribeWithGPTContentProps) => {
  const [filename, setFilename] = useState("");
  const [fileSizeBytes, setFileSizeBytes] = useState<number | null>(null);

  const [uploadPhoto, imageUploading] = useUploadPhotoForAiQuery({
    onComplete: data => {
      setPresignedGetImageUrl(data);
    },
  });

  const handleUpload = async (imageUrl: string, uploadedFilename: string, fileSize?: number) => {
    // Clear prior upload so Transcribe can't run against a stale URL mid-replacement.
    setPresignedGetImageUrl("");
    setFilename("");
    setFileSizeBytes(null);
    try {
      await uploadPhoto(imageUrl);
      trackEvent("Nutrition | Food Management | Recipe | AI Recipe Transcribe | Upload Image", {});
      setFilename(uploadedFilename);
      setFileSizeBytes(typeof fileSize === "number" ? fileSize : null);
    } catch (e) {
      if (e instanceof TranscribeError) {
        setTranscriberError(e);
      } else {
        onError(e);
      }
    }
  };

  const deleteFile = () => {
    setFilename("");
    setFileSizeBytes(null);
    setPresignedGetImageUrl("");
  };

  const handleCloseError = () => {
    // uploader is disabled based on this error, so we do not want to reset it
    // if that is the error
    if (transcriberError && transcriberError.name === "UPLOAD_INIT_ERROR") {
      return;
    }
    setTranscriberError(null);
  };

  const disableImageUploader = !!imageUploading || (transcriberError && transcriberError.name === "UPLOAD_INIT_ERROR");

  const theme = useTheme();
  const fileReadyLabel =
    fileSizeBytes !== null
      ? `${formatRecipeImageFileSize(fileSizeBytes)} · ready to transcribe`
      : "Ready to transcribe";

  return (
    <ColumnFlexBox gap={3}>
      {transcriberError && <ErrorBox errorMessage={transcriberError.message} onClose={handleCloseError} />}
      {mode === "photo" && (
        <ColumnFlexBox gap={2}>
          <FileOrImageDragAndDrop
            onUpload={handleUpload}
            fileType="image"
            loading={!!imageUploading}
            disabled={!!disableImageUploader}
            sx={{ minHeight: "114px" }}
          />
          {presignedGetImageUrl && // only show once image is uploaded
            filename && (
              <RowFlexBox sx={{ borderRadius: "4px", backgroundColor: theme.palette.greyscale[100] }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={400} noWrap>
                    {filename}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} aria-hidden />
                    <Typography variant="caption" color="text.secondary">
                      {fileReadyLabel}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={deleteFile} aria-label="Remove uploaded photo" sx={{ padding: 0.5, minWidth: 24, minHeight: 24 }}>
                  <DeleteIcon sx={{ color: theme.palette.greyscale[500] }} />
                </IconButton>
              </RowFlexBox>
            )}
        </ColumnFlexBox>
      )}
      {mode === "link" && (
        <TextField
          id="recipe-import-url"
          error={invalidUrl}
          helperText={invalidUrl ? "Invalid URL" : undefined}
          onChange={e => onChangeRecipeUrl(e.target.value)}
          value={recipeUrl}
          fullWidth
          label="Recipe URL"
          placeholder="Paste a recipe URL"
          InputLabelProps={{ sx: { fontSize: 14, fontWeight: 600, lineHeight: 1.4 } }}
        />
      )}
    </ColumnFlexBox>
  );
};
